export const SAVED_EVAL_POLL_MS = 20_000;

export type EvalResultSummary = {
  meanCostUsdPerQuestion: number | null;
  totalScore: number | null;
};

export type EvalQuestionRow = {
  question: string;
  answer: string;
};

export type PendingEvalWait = {
  metric: string;
  max_questions: number;
  agents: string[];
  previousFingerprintByAgent: Record<string, string | null>;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function evalResultSummary(results: unknown): EvalResultSummary {
  const aggregate = asRecord(asRecord(results)?.aggregate_results);
  return {
    meanCostUsdPerQuestion: asNumber(aggregate?.mean_cost_usd_per_question),
    totalScore: asNumber(aggregate?.total_score),
  };
}

export function evalResultFingerprint(results: unknown): string | null {
  if (results == null) return null;
  const aggregate = asRecord(asRecord(results)?.aggregate_results);
  const runId = aggregate?.run_id;
  const evaluatedAt = aggregate?.evaluation_datetime;
  if (typeof runId === 'string' && runId && typeof evaluatedAt === 'string') {
    return `${runId}:${evaluatedAt}`;
  }
  if (typeof runId === 'string' && runId) return runId;
  if (typeof evaluatedAt === 'string') return evaluatedAt;
  return JSON.stringify(results);
}

export function evalResultQuestions(results: unknown): EvalQuestionRow[] {
  const questions = asRecord(results)?.questions;
  if (!Array.isArray(questions)) return [];
  const rows: EvalQuestionRow[] = [];
  for (const item of questions) {
    const question = asRecord(item);
    if (question == null || typeof question.question !== 'string') continue;
    rows.push({
      question: question.question,
      answer: formatAnswer(question.response),
    });
  }
  return rows;
}

function formatAnswer(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((part) => typeof part === 'string')) {
    return value.join('\n');
  }
  return '';
}

export function formatEvalCost(value: number | null): string {
  return value == null ? '—' : value.toFixed(6);
}

export function formatEvalScore(value: number | null): string {
  return value == null ? '—' : value.toFixed(2);
}

export function isAgentEvalWaiting(
  pending: PendingEvalWait | null,
  agent: string,
  results: unknown
): boolean {
  if (pending == null || !pending.agents.includes(agent)) return false;
  return (
    evalResultFingerprint(results) ===
    (pending.previousFingerprintByAgent[agent] ?? null)
  );
}

export function stillWaitingForEvalResults(
  pending: PendingEvalWait | null,
  runs: { agent: string; results: unknown }[] | undefined
): boolean {
  if (pending == null || pending.agents.length === 0) return false;
  if (runs == null) return true;
  return pending.agents.some((agent) => {
    const run = runs.find((item) => item.agent === agent);
    return isAgentEvalWaiting(pending, agent, run?.results);
  });
}

export type EvalRunButtonState =
  | 'starting'
  | 'running'
  | 'run'
  | 'run_missing'
  | 'run_again';

const RUN_BUTTON_LABEL: Record<EvalRunButtonState, string> = {
  starting: 'Starting…',
  running: 'Running…',
  run: 'Run',
  run_missing: 'Run missing evals',
  run_again: 'Run again',
};

export function evalRunButtonLabel(state: EvalRunButtonState): string {
  return RUN_BUTTON_LABEL[state];
}

function uniqueAgentNames(agents: readonly string[]): string[] {
  const names: string[] = [];
  for (const agent of agents) {
    if (agent && !names.includes(agent)) names.push(agent);
  }
  return names;
}

function resultsForAgent(
  runs: { agent: string; results: unknown }[] | undefined,
  agent: string
): unknown {
  return runs?.find((run) => run.agent === agent)?.results;
}

export function evalRunButtonState({
  isStarting,
  selectedAgents,
  runs,
  pending,
}: {
  isStarting: boolean;
  selectedAgents: readonly string[];
  runs: { agent: string; results: unknown }[] | undefined;
  pending: PendingEvalWait | null;
}): EvalRunButtonState {
  if (isStarting) return 'starting';
  if (stillWaitingForEvalResults(pending, runs)) return 'running';

  const selected = uniqueAgentNames(selectedAgents);
  if (selected.length === 0) return 'run';

  // In-flight or already-finished agents from this start are not "missing".
  const neverRun = selected.filter((agent) => {
    if (pending?.agents.includes(agent)) return false;
    return resultsForAgent(runs, agent) == null;
  });
  if (neverRun.length === 0) return 'run_again';
  if (neverRun.length === selected.length) return 'run';
  return 'run_missing';
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function openEvalResultWindow(results: unknown): void {
  const json = JSON.stringify(results, null, 2);
  const popup = window.open('', '_blank');
  if (popup == null) return;
  popup.document.open();
  popup.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Eval report</title>
  <style>
    body { margin: 0; }
    pre {
      margin: 0;
      padding: 16px;
      font: 13px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
      white-space: pre-wrap;
      word-break: break-word;
    }
  </style>
</head>
<body><pre>${escapeHtml(json)}</pre></body>
</html>`);
  popup.document.close();
}
